import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { getCache, setCache } from "@/lib/cache";
import { brl } from "@/lib/format";
import { STATUS } from "@/lib/status";
import { FORMAS } from "@/lib/status";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, ChevronRight, Users, AlertTriangle, CalendarClock, TrendingUp, TrendingDown, ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";

export default function Dashboard() {
  const [summary, setSummary] = useState(() => getCache("dash-summary") ?? null);
  const [clientes, setClientes] = useState(() => getCache("dash-clientes") ?? []);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("ativos");
  const navigate = useNavigate();

  const [diaOpen, setDiaOpen] = useState(false);
  const [diaMode, setDiaMode] = useState("dia");
  const [diaData, setDiaData] = useState("");
  const [diaMes, setDiaMes] = useState("");
  const [diaResult, setDiaResult] = useState(null);
  const fetchDia = async (d) => {
    setDiaData(d);
    if (!d) return;
    try { const { data } = await api.get(`/dashboard/recebido-dia?data=${d}`); setDiaResult(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const fetchMes = async (m) => {
    setDiaMes(m);
    if (!m) return;
    try { const { data } = await api.get(`/dashboard/recebido-mes?mes=${m}`); setDiaResult(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const switchDiaMode = (mode) => {
    setDiaMode(mode); setDiaResult(null);
    if (mode === "dia") fetchDia(diaData || new Date().toISOString().slice(0, 10));
    else fetchMes(diaMes || new Date().toISOString().slice(0, 7));
  };
  const openDia = () => { setDiaOpen(true); setDiaMode("dia"); setDiaResult(null); fetchDia(new Date().toISOString().slice(0, 10)); };

  const [receberOpen, setReceberOpen] = useState(false);
  const [receberDe, setReceberDe] = useState("");
  const [receberData, setReceberData] = useState("");
  const [receberResult, setReceberResult] = useState(null);
  const fetchReceber = async (de, ate) => {
    setReceberDe(de); setReceberData(ate);
    if (!ate) return;
    try { const { data } = await api.get(`/dashboard/a-receber-ate`, { params: { data: ate, de: de || "" } }); setReceberResult(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const openReceber = () => { setReceberOpen(true); setReceberResult(null); const today = new Date().toISOString().slice(0, 10); fetchReceber("", today); };

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([api.get("/dashboard/fichas-summary"), api.get("/clientes")]);
      setSummary(s.data); setCache("dash-summary", s.data);
      setClientes(c.data); setCache("dash-clientes", c.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ativos = clientes.filter((c) => !c.liquidado);
  const liquidados = clientes.filter((c) => c.liquidado);
  const alerta = clientes.filter((c) => c.em_alerta && !c.liquidado);
  const base = tab === "ativos" ? ativos : tab === "liquidados" ? liquidados : alerta;
  const filtered = base.filter((c) => {
    if (!q) return true;
    const cl = c.cliente || {};
    const hay = `${c.nome} ${cl.telefone || ""} ${cl.endereco || ""} ${cl.bairro || ""} ${cl.cidade || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const cards = summary ? [
    { label: "A receber hoje", value: brl(summary.a_receber_hoje), cls: "text-yellow-400", icon: CalendarClock, testid: "card-a-receber-hoje", onClick: () => navigate("/painel/vencimentos?f=hoje") },
    { label: "Recebido no dia", value: brl(summary.recebido_hoje), cls: "text-green-400", icon: TrendingDown, testid: "card-recebido-hoje", onClick: openDia },
    { label: "Total a receber", value: brl(summary.total_a_receber), cls: "text-primary", icon: TrendingUp, testid: "card-a-receber", onClick: openReceber },
    { label: "Clientes", value: summary.qtd_clientes, cls: "text-foreground", icon: Users, testid: "card-clientes", onClick: () => document.querySelector('[data-testid="clientes-list"]')?.scrollIntoView({ behavior: "smooth" }) },
    { label: "Vencidas", value: summary.prestacoes_vencidas, cls: "text-red-400", icon: AlertTriangle, testid: "card-vencidas", onClick: () => navigate("/painel/vencimentos?f=vencidas") },
    { label: "Vencem hoje", value: summary.vencem_hoje, cls: "text-yellow-400", icon: CalendarClock, testid: "card-vencem-hoje", onClick: () => navigate("/painel/vencimentos?f=hoje") },
  ] : [];

  return (
    <AdminLayout>
      <div className="mb-6">
        <p className="text-muted-foreground font-medium">Controle de crediário</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-foreground">Painel</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <motion.div key={c.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              onClick={c.onClick} role="button" tabIndex={0}
              data-testid={c.testid} className="rounded-2xl border border-border bg-card p-4 cursor-pointer hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium"><Icon className="w-3.5 h-3.5" /> {c.label}</div>
              <p className={`font-display text-2xl sm:text-3xl font-black mt-1 ${c.cls}`}>{c.value}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="flex items-center gap-1 mb-4 bg-card border border-border rounded-full p-1 w-fit">
        {[
          { k: "ativos", label: "Ativos", count: ativos.length },
          { k: "liquidados", label: "Liquidados", count: liquidados.length },
          { k: "alerta", label: "Alerta", count: alerta.length },
        ].map((t) => (
          <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`px-3 py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${tab === t.k ? (t.k === "alerta" ? "bg-orange-500 text-white" : "bg-primary text-primary-foreground") : (t.k === "alerta" && t.count > 0 ? "text-orange-400 hover:text-orange-300" : "text-muted-foreground hover:text-foreground")}`}>
            {t.k === "alerta" && <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}{t.label} ({t.count})
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-5 h-5 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
          <Input data-testid="search-ficha" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Nome, telefone, endereço..." className="pl-12 rounded-full bg-card" style={{ height: 52 }} />
        </div>
        <Button data-testid="nova-ficha-button" onClick={() => navigate("/painel/nova-ficha")} className="rounded-full font-semibold shrink-0" style={{ height: 52 }}>
          <Plus className="w-5 h-5" /> <span className="hidden sm:inline">Nova ficha</span>
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="clientes-list">
        {filtered.length === 0 && <div className="p-12 text-center text-muted-foreground">{tab === "ativos" ? "Nenhum cliente ativo." : tab === "liquidados" ? "Nenhum cliente liquidado." : "Nenhuma ficha com 60+ dias de atraso. 🎉"}</div>}
        {filtered.map((c, i) => {
          const st = STATUS[c.status] || STATUS.pendente;
          return (
            <button key={c.key} data-testid={`cliente-row-${i}`} onClick={() => navigate(`/painel/cliente/${encodeURIComponent(c.key)}`)}
              className={`w-full flex items-center justify-between px-5 py-4 text-left hover:bg-secondary transition-colors ${i !== filtered.length - 1 ? "border-b border-border" : ""}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${st.dot}`} />
                <div className="min-w-0">
                  <p className="font-semibold text-foreground truncate">{c.nome}</p>
                  <p className="text-sm text-muted-foreground truncate flex items-center gap-1.5">
                    <ShoppingBag className="w-3.5 h-3.5" /> {c.num_fichas} compra{c.num_fichas > 1 ? "s" : ""}
                    {(c.cliente?.telefone) ? ` · ${c.cliente.telefone}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <p className={`font-display font-bold ${c.saldo_total > 0 ? "text-red-400" : "text-green-400"}`}>{brl(c.saldo_total)}</p>
                  {tab === "alerta" && c.dias_atraso > 0
                    ? <p className="text-xs font-bold text-orange-400">{c.dias_atraso} dias de atraso</p>
                    : <p className={`text-xs font-medium ${st.text}`}>{c.liquidado ? "Liquidado" : st.label}</p>}
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={diaOpen} onOpenChange={setDiaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Recebido</DialogTitle><DialogDescription>Veja quanto você recebeu num dia ou no mês inteiro.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-1 bg-card border border-border rounded-full p-1 w-fit">
              <button type="button" data-testid="dia-mode-dia" onClick={() => switchDiaMode("dia")} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${diaMode === "dia" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Dia</button>
              <button type="button" data-testid="dia-mode-mes" onClick={() => switchDiaMode("mes")} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${diaMode === "mes" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Mês</button>
            </div>
            {diaMode === "dia"
              ? <div><Label>Data</Label><Input data-testid="dia-data" type="date" value={diaData} onChange={(e) => fetchDia(e.target.value)} className="mt-1.5 bg-card" style={{ height: 48 }} /></div>
              : <div><Label>Mês</Label><Input data-testid="dia-mes" type="month" value={diaMes} onChange={(e) => fetchMes(e.target.value)} className="mt-1.5 bg-card" style={{ height: 48 }} /></div>}
            <div className="rounded-2xl bg-primary/10 border border-primary/30 p-5 text-center">
              <p className="text-muted-foreground text-sm">{diaMode === "dia" ? `Total recebido ${diaData ? `em ${diaData.split("-").reverse().join("/")}` : ""}` : `Total recebido ${diaMes ? `em ${diaMes.split("-").reverse().join("/")}` : "no mês"}`}</p>
              <p className="font-display text-4xl font-black text-primary mt-1" data-testid="dia-total">{diaResult ? brl(diaResult.total) : "—"}</p>
              {diaResult && <p className="text-xs text-muted-foreground mt-1">{diaResult.qtd} recebimento{diaResult.qtd === 1 ? "" : "s"}</p>}
            </div>
            {diaResult && diaResult.itens && diaResult.itens.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-xl border border-border divide-y divide-border" data-testid="dia-lista">
                {diaResult.itens.map((it, i) => (
                  <button key={i} type="button" data-testid={`dia-item-${i}`} onClick={() => { setDiaOpen(false); navigate(`/painel/cliente/${encodeURIComponent(it.ficha_id || it.nome)}`); }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors">
                    <span className="text-foreground truncate">{it.nome}<span className="text-muted-foreground"> · {diaMode === "mes" && it.data ? `${it.data.split("-").reverse().slice(0, 2).join("/")} · ` : ""}{FORMAS[it.forma] || it.forma}</span></span>
                    <span className="font-display font-bold text-green-400 shrink-0 ml-2">{brl(it.valor)}</span>
                  </button>
                ))}
              </div>
            )}
            {diaResult && diaResult.itens && diaResult.itens.length === 0 && (
              <p className="text-center text-sm text-muted-foreground">Nenhum recebimento {diaMode === "dia" ? "nesse dia" : "nesse mês"}.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={receberOpen} onOpenChange={setReceberOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Total a receber por período</DialogTitle><DialogDescription>Escolha o intervalo de datas para ver quanto ainda falta receber de parcelas que vencem no período.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>De (opcional)</Label><Input data-testid="receber-de" type="date" value={receberDe} onChange={(e) => fetchReceber(e.target.value, receberData)} className="mt-1.5 bg-card" style={{ height: 48 }} /></div>
              <div><Label>Até a data</Label><Input data-testid="receber-data" type="date" value={receberData} onChange={(e) => fetchReceber(receberDe, e.target.value)} className="mt-1.5 bg-card" style={{ height: 48 }} /></div>
            </div>
            <div className="rounded-2xl bg-primary/10 border border-primary/30 p-5 text-center">
              <p className="text-muted-foreground text-sm">A receber {receberDe ? `de ${receberDe.split("-").reverse().join("/")} ` : ""}até {receberData ? receberData.split("-").reverse().join("/") : ""}</p>
              <p className="font-display text-4xl font-black text-primary mt-1" data-testid="receber-total">{receberResult ? brl(receberResult.total) : "—"}</p>
              {receberResult && <p className="text-xs text-muted-foreground mt-1">{receberResult.qtd} parcela{receberResult.qtd === 1 ? "" : "s"}</p>}
            </div>
            {receberResult && receberResult.itens && receberResult.itens.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-xl border border-border divide-y divide-border" data-testid="receber-lista">
                {receberResult.itens.map((it, i) => (
                  <button key={i} type="button" data-testid={`receber-item-${i}`} onClick={() => { setReceberOpen(false); navigate(`/painel/cliente/${encodeURIComponent(it.ficha_id || it.nome)}`); }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors">
                    <span className="text-foreground truncate">{it.nome}<span className="text-muted-foreground"> · vence {it.vencimento.split("-").reverse().join("/")}</span></span>
                    <span className="font-display font-bold text-red-400 shrink-0 ml-2">{brl(it.valor)}</span>
                  </button>
                ))}
              </div>
            )}
            {receberResult && receberResult.itens && receberResult.itens.length === 0 && (
              <p className="text-center text-sm text-muted-foreground">Nada a receber até essa data.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}